import { useState, useEffect } from "react";
import { PortfolioItem } from "@/hooks/useProfile";
import { usePortfolioState } from "./hooks/usePortfolioState";
import { usePortfolioItems } from "./hooks/usePortfolioItems";
import { usePortfolioImages } from "./hooks/usePortfolioImages";

interface UsePortfolioSectionProps {
  initialItems: PortfolioItem[] | null;
  onPortfolioChange: (portfolioItems: PortfolioItem[]) => void;
  sectionId?: string;
}

export function usePortfolioSection({
  initialItems,
  onPortfolioChange,
  sectionId = "portfolio",
  isVerified = false
}: UsePortfolioSectionProps & { isVerified?: boolean }) {
  const [storageStatus, setStorageStatus] = useState({
    isAccessible: null as boolean | null,
    errorDetails: null as string | null,
    isChecked: false,
  });

  // UI state management
  const {
    isAdding,
    isEditing,
    editingImageId,
    handleAddFirstProject,
    handleCancelAdd,
    handleEditItem,
    handleCancelEdit,
    handleEditImage,
    handleCancelEditImage,
    setIsAdding,
    resetUIState,
  } = usePortfolioState(sectionId);

  // Portfolio items management
  const {
    handleAddItem,
    handleUpdateItem,
    handleRemoveItem,
    handleRenameItem,
    handleReorderItems,
    handleSetFeaturedItem,
    handleUpdateStatus,
    isProcessing,
  } = usePortfolioItems(initialItems, onPortfolioChange, isVerified);

  // Portfolio images management
  const {
    handleAddImage,
    handleRemoveImage,
    handleReorderImages,
    handleMoveImage,
    handleUpdateImageCaption,
  } = usePortfolioImages(initialItems, onPortfolioChange);

  return {
    // UI state
    isAdding,
    isEditing,
    editingImageId,

    // Item management
    handleAddItem,
    handleUpdateItem,
    handleRemoveItem,
    handleRenameItem,
    handleReorderItems,
    handleSetFeaturedItem,
    handleUpdateStatus,
    isProcessing,

    // Image management
    handleAddImage,
    handleRemoveImage,
    handleReorderImages,
    handleMoveImage,
    handleUpdateImageCaption,

    // UI actions
    handleAddFirstProject,
    handleCancelAdd,
    handleEditItem,
    handleCancelEdit,
    handleEditImage,
    handleCancelEditImage,
    setIsAdding,
    resetUIState,

    // Storage status
    isStorageAccessible: storageStatus.isAccessible,
    storageErrorDetails: storageStatus.errorDetails,
    policyCheckCompleted: storageStatus.isChecked,
  };
}
